// Platform Admin — Phase 5 security & platform (ADMIN_UPGRADE_PLAN 10, 17, 19, 44, 50).
// Staff access, platform status, feature flags, retention policy, announcement,
// and the two-step GDPR tenant purge.
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import {
  listStaff, grantStaff, revokeStaff,
  getPlatformConfig, setPlatformConfig,
  previewPurge, purgeTenant,
  type StaffMember, type StaffRole, type PlatformConfig, type PurgePreview, type TaxConfig,
} from '../../lib/padminSecurity';
import { usePadminTenants } from '../../lib/padmin';

const CARD = 'rounded-card border border-slate-200 bg-white p-5 shadow-sm';
const FLD = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary';
const BTN = 'rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40';
const GHOST = 'rounded-[8px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-ink-2 hover:border-primary hover:text-primary disabled:opacity-40';
const DANGER = 'rounded-[9px] bg-red-600 px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40';

function Err({ msg }: { msg: string | null }) {
  if (!msg) return null;
  return <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{msg}</div>;
}

// ── 19. Staff access ─────────────────────────────────────────────────────────
function StaffCard() {
  const { t } = useI18n();
  const [staff, setStaff] = useState<StaffMember[] | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRole>('support');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    listStaff().then((r) => setStaff(r.staff)).catch((e: Error) => setErr(e.message));
  }, []);
  useEffect(load, [load]);

  async function grant() {
    const reason = window.prompt(t('pa.sec.grantPrompt'));
    if (!reason || reason.trim().length < 3) return;
    setBusy(true); setErr(null);
    try {
      await grantStaff({ email: email.trim(), role, reason: reason.trim() });
      setEmail(''); load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  async function revoke(m: StaffMember) {
    const reason = window.prompt(t('pa.sec.revokePrompt'));
    if (!reason || reason.trim().length < 3) return;
    setBusy(true); setErr(null);
    try { await revokeStaff(m.uid, reason.trim()); load(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div className={cn(CARD, 'space-y-3')}>
      <div>
        <b className="block text-sm text-ink">{t('pa.sec.staff')}</b>
        <p className="text-xs text-ink-3">{t('pa.sec.staffNote')}</p>
      </div>
      <Err msg={err} />

      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-[220px] flex-1">
          <span className="mb-1 block text-[11px] font-bold text-ink-2">{t('pa.mem.inviteEmail')}</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="name@tagnologys.com" className={FLD} />
        </label>
        <label>
          <span className="mb-1 block text-[11px] font-bold text-ink-2">{t('pa.mem.inviteRole')}</span>
          <select value={role} onChange={(e) => setRole(e.target.value as StaffRole)}
            className="rounded-[9px] border border-slate-200 px-3 py-2 text-sm font-bold">
            <option value="support">{t('pa.sec.role.support')}</option>
            <option value="finance">{t('pa.sec.role.finance')}</option>
            <option value="superadmin">{t('pa.sec.role.superadmin')}</option>
          </select>
        </label>
        <button type="button" onClick={() => void grant()} disabled={busy || !email.includes('@')} className={BTN}>
          {busy ? '…' : t('pa.sec.grant')}
        </button>
      </div>

      {staff === null ? <div className="py-4 text-center text-sm text-ink-3">…</div> : (
        <div className="divide-y divide-slate-100">
          {staff.length === 0 && <p className="py-2 text-xs text-ink-3">{t('pa.sec.staffEmpty')}</p>}
          {staff.map((m) => (
            <div key={m.uid} className="flex flex-wrap items-center gap-2 py-2.5">
              <div className="min-w-0 flex-1">
                <b className="block truncate text-sm text-ink">{m.email ?? m.uid}</b>
                <span className="text-[11px] text-ink-3">
                  {t('pa.sec.grantedBy')} {m.grantedBy ?? '—'} · {m.lastSignIn ? new Date(m.lastSignIn).toLocaleDateString() : '—'}
                </span>
              </div>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-ink-2">{t(`pa.sec.role.${m.role}`)}</span>
              {m.isRoot && <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-bold text-primary">{t('pa.sec.root')}</span>}
              {!m.claimOk && !m.isRoot && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">{t('pa.sec.claimMissing')}</span>}
              {!m.isRoot && (
                <button type="button" disabled={busy} onClick={() => void revoke(m)}
                  className={cn(GHOST, 'hover:border-red-300 hover:text-red-600')}>
                  {t('pa.sec.revoke')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-ink-3">{t('pa.sec.claimNote')}</p>
    </div>
  );
}

// ── 50 + 44. Platform config ─────────────────────────────────────────────────
function ConfigCard() {
  const { t } = useI18n();
  const [cfg, setCfg] = useState<PlatformConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // editable copies
  const [retEnabled, setRetEnabled] = useState(false);
  const [retDays, setRetDays] = useState('365');
  const [annEnabled, setAnnEnabled] = useState(false);
  const [annText, setAnnText] = useState('');
  const [annAr, setAnnAr] = useState('');
  const [tax, setTax] = useState<TaxConfig>({
    enabled: false, rates: {}, defaultRatePct: 0, label: 'VAT', registrationNumber: '',
  });
  const [flags, setFlags] = useState<Record<string, boolean>>({});

  const load = useCallback(() => {
    getPlatformConfig().then((c) => {
      setCfg(c);
      setRetEnabled(c.retention.enabled);
      setRetDays(String(c.retention.conversationDays || 365));
      setAnnEnabled(c.announcement.enabled);
      setAnnText(c.announcement.text);
      setAnnAr(c.announcement.textAr);
      setTax(c.tax);
      setFlags(c.flags ?? {});
    }).catch((e: Error) => setErr(e.message));
  }, []);
  useEffect(load, [load]);

  async function save() {
    const reason = window.prompt(t('pa.sec.cfgPrompt'));
    if (!reason || reason.trim().length < 3) return;
    setBusy(true); setErr(null); setSaved(false);
    try {
      await setPlatformConfig({
        retention: { enabled: retEnabled, conversationDays: Number(retDays) || 0 },
        announcement: { enabled: annEnabled, text: annText.trim(), textAr: annAr.trim() },
        tax,
        flags,
        reason: reason.trim(),
      });
      setSaved(true); window.setTimeout(() => setSaved(false), 1800); load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  function setRate(cc: string, pct: number) {
    setTax((prev) => ({ ...prev, rates: { ...prev.rates, [cc]: pct } }));
  }
  function removeRate(cc: string) {
    setTax((prev) => {
      const rates = { ...prev.rates };
      delete rates[cc];
      return { ...prev, rates };
    });
  }

  return (
    <div className={cn(CARD, 'space-y-4')}>
      <b className="block text-sm text-ink">{t('pa.sec.platform')}</b>
      <Err msg={err} />

      {cfg && (
        <div className="flex flex-wrap gap-2">
          <span className={cn('rounded-[9px] border px-3 py-2 text-xs font-bold',
            cfg.status.stuckSending > 0 ? 'border-red-300 bg-red-50 text-red-700' : 'border-slate-200 text-ink-3')}>
            {cfg.status.stuckSending} {t('pa.sec.stuck')}
          </span>
          <span className={cn('rounded-[9px] border px-3 py-2 text-xs font-bold',
            cfg.status.overdueScheduled > 0 ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-slate-200 text-ink-3')}>
            {cfg.status.overdueScheduled} {t('pa.sec.overdue')}
          </span>
          <span className="rounded-[9px] border border-slate-200 px-3 py-2 text-xs font-bold text-ink-3">
            {cfg.status.staffCount} {t('pa.sec.staffCount')}
          </span>
        </div>
      )}

      <div className="border-t border-slate-100 pt-3">
        <label className="flex items-center gap-2 text-sm font-semibold text-ink">
          <input type="checkbox" checked={retEnabled} onChange={(e) => setRetEnabled(e.target.checked)} />
          {t('pa.sec.retention')}
        </label>
        <p className="mt-1 text-xs text-ink-3">{t('pa.sec.retentionNote')}</p>
        {retEnabled && (
          <label className="mt-2 block max-w-[220px]">
            <span className="mb-1 block text-[11px] font-bold text-ink-2">{t('pa.sec.retentionDays')}</span>
            <input type="number" min={30} max={3650} value={retDays}
              onChange={(e) => setRetDays(e.target.value)} className={FLD} />
          </label>
        )}
      </div>

      <div className="border-t border-slate-100 pt-3">
        <label className="flex items-center gap-2 text-sm font-semibold text-ink">
          <input type="checkbox" checked={annEnabled} onChange={(e) => setAnnEnabled(e.target.checked)} />
          {t('pa.sec.announcement')}
        </label>
        <p className="mt-1 text-xs text-ink-3">{t('pa.sec.announcementNote')}</p>
        {annEnabled && (
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <input value={annText} onChange={(e) => setAnnText(e.target.value)}
              placeholder={t('pa.sec.annEnglish') || 'English'} className={FLD} />
            <input value={annAr} onChange={(e) => setAnnAr(e.target.value)}
              placeholder={t('pa.sec.annArabic') || 'العربية'} dir="rtl" className={FLD} />
          </div>
        )}
      </div>

      {/* VAT / tax */}
      <div className="border-t border-slate-100 pt-3">
        <label className="flex items-center gap-2 text-sm font-semibold text-ink">
          <input type="checkbox" checked={tax.enabled}
            onChange={(e) => setTax({ ...tax, enabled: e.target.checked })} />
          {t('pa.sec.tax')}
        </label>
        <p className="mt-1 text-xs text-ink-3">{t('pa.sec.taxNote')}</p>
        {tax.enabled && (
          <div className="mt-2 space-y-2">
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="block">
                <span className="mb-1 block text-[11px] font-bold text-ink-2">{t('pa.sec.taxLabel')}</span>
                <input value={tax.label} onChange={(e) => setTax({ ...tax, label: e.target.value })} className={FLD} />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] font-bold text-ink-2">{t('pa.sec.taxReg')}</span>
                <input value={tax.registrationNumber}
                  onChange={(e) => setTax({ ...tax, registrationNumber: e.target.value })} className={FLD} />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] font-bold text-ink-2">{t('pa.sec.taxDefault')}</span>
                <input type="number" min={0} max={100} value={tax.defaultRatePct}
                  onChange={(e) => setTax({ ...tax, defaultRatePct: Number(e.target.value) })} className={FLD} />
              </label>
            </div>
            <span className="block text-[11px] font-bold text-ink-2">{t('pa.sec.taxRates')}</span>
            <div className="flex flex-wrap gap-2">
              {Object.entries(tax.rates).sort(([a], [b]) => a.localeCompare(b)).map(([cc, pct]) => (
                <span key={cc} className="inline-flex items-center gap-1 rounded-[9px] border border-slate-200 px-2 py-1">
                  <b className="font-mono text-xs text-ink">{cc}</b>
                  <input type="number" min={0} max={100} value={pct}
                    onChange={(e) => setRate(cc, Number(e.target.value))}
                    className="w-14 rounded border border-slate-200 px-1 py-0.5 text-xs" />
                  <span className="text-xs text-ink-3">%</span>
                  <button type="button" onClick={() => removeRate(cc)} className="text-xs text-ink-3 hover:text-red-600">✕</button>
                </span>
              ))}
              <button type="button" className={GHOST} onClick={() => {
                const cc = window.prompt(t('pa.sec.taxAddCountry'))?.trim().toUpperCase();
                if (cc && /^[A-Z]{2}$/.test(cc)) setRate(cc, 0);
              }}>+ {t('pa.sec.taxAddCountry')}</button>
            </div>
          </div>
        )}
      </div>

      {/* Feature flags */}
      <div className="border-t border-slate-100 pt-3">
        <b className="block text-sm text-ink">{t('pa.sec.flags')}</b>
        <p className="mt-1 text-xs text-ink-3">{t('pa.sec.flagsNote')}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {Object.keys(flags).length === 0 && <p className="text-xs text-ink-3">{t('pa.sec.flagsEmpty')}</p>}
          {Object.entries(flags).sort(([a], [b]) => a.localeCompare(b)).map(([name, on]) => (
            <span key={name} className="inline-flex items-center gap-2 rounded-[9px] border border-slate-200 px-2.5 py-1.5">
              <input type="checkbox" checked={on}
                onChange={(e) => setFlags({ ...flags, [name]: e.target.checked })} />
              <span className="font-mono text-xs text-ink">{name}</span>
              <button type="button" className="text-xs text-ink-3 hover:text-red-600"
                onClick={() => setFlags((prev) => {
                  const next = { ...prev };
                  delete next[name];
                  return next;
                })}>✕</button>
            </span>
          ))}
          <button type="button" className={GHOST} onClick={() => {
            const name = window.prompt(t('pa.sec.flagName'))?.trim();
            // Must start with a letter: Firestore reserves __*__ field names, and
            // update() would reject the whole save with an opaque internal error.
            if (name && /^[a-z][a-z0-9_]{1,39}$/i.test(name)) setFlags((prev) => ({ ...prev, [name]: false }));
          }}>+ {t('pa.sec.flagAdd')}</button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Never savable before the config loaded: the editors would still hold
            their empty defaults, and writing flags:{} replaces the map — wiping
            every platform feature flag. */}
        <button type="button" onClick={() => void save()} disabled={busy || !cfg} className={BTN}>
          {busy ? '…' : t('pa.sec.save')}
        </button>
        {saved && <span className="text-xs font-bold text-green-600">✓</span>}
      </div>
    </div>
  );
}

// ── 10. GDPR purge ───────────────────────────────────────────────────────────
function PurgeCard() {
  const { t } = useI18n();
  const { tenants, reload: reloadTenants } = usePadminTenants();
  const [tenantId, setTenantId] = useState('');
  const [preview, setPreview] = useState<PurgePreview | null>(null);
  const [confirmName, setConfirmName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function step1() {
    if (!tenantId) return;
    setBusy(true); setErr(null); setPreview(null); setDone(null);
    try { setPreview(await previewPurge(tenantId)); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  async function step2() {
    if (!preview) return;
    setBusy(true); setErr(null);
    try {
      await purgeTenant({ tenantId: preview.tenantId, confirmName: confirmName.trim(), reason: reason.trim() });
      setDone(preview.tenantName);
      setPreview(null); setConfirmName(''); setReason(''); setTenantId('');
      reloadTenants(); // the purged tenant must leave the dropdown immediately
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  const canPurge = preview !== null
    && confirmName.trim() === preview.tenantName.trim()
    && reason.trim().length >= 10;

  return (
    <div className={cn(CARD, 'space-y-3 border-red-200')}>
      <div>
        <b className="block text-sm text-red-700">{t('pa.sec.purge')}</b>
        <p className="text-xs text-ink-3">{t('pa.sec.purgeNote')}</p>
      </div>
      <Err msg={err} />
      {done && (
        <div className="rounded-[9px] bg-green-50 px-3 py-2 text-xs font-semibold text-green-700">
          {t('pa.sec.purged')} {done}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-[220px] flex-1">
          <span className="mb-1 block text-[11px] font-bold text-ink-2">{t('pa.inv.col.tenant')}</span>
          <select value={tenantId} onChange={(e) => { setTenantId(e.target.value); setPreview(null); }} className={FLD}>
            <option value="">—</option>
            {tenants.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </label>
        <button type="button" onClick={() => void step1()} disabled={busy || !tenantId} className={GHOST}>
          {busy && !preview ? '…' : t('pa.sec.preview')}
        </button>
      </div>

      {preview && (
        <div className="space-y-3 rounded-[9px] border border-red-200 bg-red-50/50 p-3">
          <div>
            <b className="text-sm text-ink">{preview.tenantName}</b>
            <span className="ms-2 text-xs font-bold text-red-700">
              {preview.total.toLocaleString()} {t('pa.sec.docs')} · {preview.memberUids.length} {t('pa.sec.members')}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(preview.counts).map(([k, n]) => (
              <span key={k} className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-ink-2">
                {k}: {n.toLocaleString()}
              </span>
            ))}
          </div>
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold text-ink-2">
              {t('pa.sec.typeName')} <code className="font-mono text-red-700">{preview.tenantName}</code>
            </span>
            <input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} className={FLD} />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold text-ink-2">{t('pa.sec.purgeReason')}</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} className={FLD} />
          </label>
          <button type="button" onClick={() => void step2()} disabled={busy || !canPurge} className={DANGER}>
            {busy ? '…' : t('pa.sec.purgeConfirm')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function SecurityPanel() {
  return (
    <div className="space-y-4">
      <StaffCard />
      <ConfigCard />
      <PurgeCard />
    </div>
  );
}
